import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/utils/query-keys';
import { useSetConsolidateBeraSummary } from '@/hooks/useSetConsolidateBeraSummary';
import { useDownloadBeraJobSummaryV2Pdf } from '@/hooks/useDownloadBeraJobSummaryV2Pdf';
import { useComments } from '@/components/views/BeraJob/Results/hooks';
import type { BeraSummaryResult } from '@/types/Bera';

import { useDebouncedComment } from '@/components/views/BeraJob/BeraReportV2/_hooks';
import {
	DEFAULT_LOCALE,
	EMPTY_TEXT,
	SUMMARY_COMMENT_DEBOUNCE_MS
} from '@/components/views/BeraJob/BeraReportV2/constants';

interface FinalResultActionsParams {
	organization_id: string;
	company_id: string;
	job_summary_id: string;
	summary?: BeraSummaryResult;
}

export function useFinalResultActions({
	organization_id,
	company_id,
	job_summary_id,
	summary
}: FinalResultActionsParams) {
	const queryClient = useQueryClient();

	const [isConsolidated, setIsConsolidated] = useState<boolean>(false);

	const loadedSummaryFor = useRef<string | null>(null);

	const { comment, setComment, handleCommentChange, handleCommentBlur } = useDebouncedComment({
		delay: SUMMARY_COMMENT_DEBOUNCE_MS,
		onSave: saveComment
	});

	const { mutateAsync: consolidateSummary, isLoading: isConsolidating } = useSetConsolidateBeraSummary();
	const { mutateAsync: downloadPdf, isLoading: isDownloading } = useDownloadBeraJobSummaryV2Pdf();
	const { mutateAsync: saveComments } = useComments();

	const locale = window.navigator.language ?? DEFAULT_LOCALE;

	useEffect(() => {
		if (!summary?.id || loadedSummaryFor.current === summary.id) {
			return;
		}

		loadedSummaryFor.current = summary.id;
		setComment(summary.comment ?? EMPTY_TEXT);
		setIsConsolidated(summary.consolidated);
	}, [summary]);

	async function saveComment(value: string): Promise<void> {
		const params = { organization_id, company_id, id: job_summary_id, comment: value };

		await saveComments(params);
	}

	async function handleToggleConsolidate(): Promise<void> {
		const isConsolidatedNow = !isConsolidated;
		const params = {
			id: job_summary_id,
			organization_id,
			company_id,
			locale,
			consolidated: isConsolidatedNow
		};

		await consolidateSummary(params);

		setIsConsolidated(isConsolidatedNow);
		queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_SUMMARY_RESULT]);
		message.success(I18n.get('Report updated successfully'));
	}

	async function handleDownloadPdf(): Promise<void> {
		const params = { id: job_summary_id, organization_id, company_id, locale };

		await downloadPdf(params);
	}

	return {
		comment,
		isConsolidated,
		isConsolidating,
		isDownloading,
		handleCommentChange,
		handleCommentBlur,
		handleToggleConsolidate,
		handleDownloadPdf
	};
}
